/**
 * Предстарт окна: как показывать оценку, пока страйк ещё формируется.
 *
 * Чистый модуль без React по правилу lib/*.ts: vitest включает только
 * `*.test.ts`, и всё, что решает, какое число и какую подпись видит оператор,
 * обязано лежать под тестом. Модель посчитана в Go (lock.ComputePreopen) и
 * приезжает готовой — здесь только показ.
 *
 * # Что тут за физика, в двух фразах
 *
 * Страйк — та же метрика, что итог, только с меткой начала окна: отчёт TWAP-W с
 * меткой t0 усредняет сырой ряд по `[t0−W−2, t0−3]`. Пока эти секунды идут,
 * стакан следующего окна переоценивается вслед за формирующимся страйком, а до
 * их начала ход цены на исход не влияет вовсе — он сдвигает страйк и итог на
 * одну и ту же величину.
 *
 * Отсюда три состояния яруса, и различать их обязательно: «до окна страйка N с»
 * (исход — монетка, стакан ездит не по делу), «страйк неизвестен N с» (идёт
 * переоценка) и «страйк определён» (последние три секунды до открытия).
 */
import type { Valuation, WindowPreopen } from "../api/types";
import { parse, SCALE } from "./money";
import { cushionAmount, cushionArrow, percentOf } from "./twaplock";
import { formatDeviationMagnitude } from "./valuation";

/** Оценка стоит на прогнозе страйка, а не на записанном страйке. */
export function isStrikeForecast(v: Valuation | undefined): boolean {
  return Boolean(v?.strike_forecast);
}

/**
 * Подпись страйка: `K̂` у прогноза, `K` у факта.
 *
 * Крышка над буквой — то же обозначение оценки, что у `Ŝ` ярусом ниже, и оно
 * обязательно: число, которое ещё двигается, под подписью `K` читалось бы как
 * зафиксированный страйк, и отклонение от него — как отклонение от факта.
 */
export function strikeTag(v: Valuation | undefined): string {
  return isStrikeForecast(v) ? "K̂" : "K";
}

/** Пояснение к подписи страйка — в подсказку. */
export function strikeTagTitle(v: Valuation | undefined): string {
  if (!isStrikeForecast(v)) return "страйк — цена базового актива на начало окна";
  return (
    "ПРОГНОЗ страйка: окно ещё не открылось, и страйк формируется как TWAP по " +
    "секундам перед открытием. Уже прошедшие секунды его окна взяты как есть, " +
    "ненаступившие — по последней цене"
  );
}

/**
 * Разброс прогноза страйка: «±12.40». Пусто, когда σ не оценена или разброса
 * нет (все секунды окна страйка уже прошли).
 *
 * Знаков столько же, сколько у самого страйка: разброс сравнивают глазом с
 * отклонением рядом, а оно печатается по масштабу индекса.
 */
export function strikeSigmaLabel(v: Valuation | undefined): string {
  const sigma = v?.preopen?.strike_sigma;
  const m = parse(sigma);
  if (m === null || m <= 0) return "";
  return `±${formatDeviationMagnitude(sigma, v?.strike)}`;
}

/**
 * Подробная подсказка к прогнозу страйка: чем он посчитан.
 *
 * Покрытие — числом, а не словом «частично»: «прошло 12 из 60 секунд» и «прошло
 * 58 из 60» — разные степени доверия к одному и тому же числу на экране.
 */
export function strikeForecastTitle(v: Valuation | undefined): string {
  const p = v?.preopen;
  if (!p) return "";
  const parts = [
    `прогноз страйка: в ряду ${p.strike_have} из ${p.strike_want} секунд его окна`,
  ];
  const sigma = strikeSigmaLabel(v);
  if (sigma !== "") parts.push(`разброс прогноза ${sigma}`);
  const ahead = p.ahead?.strike;
  if (ahead && !sameCents(ahead, v?.strike)) {
    parts.push(`по ряду, достроенному спотом биржи, — ${ahead}`);
  }
  if ((p.strike_lost ?? 0) > 0) {
    parts.push(`потеряно секунд окна страйка: ${p.strike_lost}`);
  }
  return parts.join("; ");
}

/** Фаза предстарта для яруса под строкой индекса. */
export interface PreopenPhase {
  /** Подпись поля. */
  label: string;
  /** Число секунд. null — считать нечего (страйк уже определён). */
  seconds: number | null;
  /** Развёрнутое объяснение — в подсказку. */
  title: string;
}

/**
 * Какая часть предстарта идёт и сколько ей осталось.
 *
 * Показывается то число, которое ДВИЖЕТСЯ и нужно для решения. До начала окна
 * страйка — через сколько оно начнётся: раньше этого момента стакан ездит не по
 * делу, и его переоценка ничего не говорит об исходе. Внутри окна — сколько
 * страйка ещё неизвестно.
 */
export function preopenPhase(p: WindowPreopen | undefined): PreopenPhase | null {
  if (!p) return null;
  const dead = Math.ceil((p.strike_dead_prefix_ms ?? 0) / 1000);
  if (dead > 0) {
    return {
      label: "до окна страйка",
      seconds: dead,
      title:
        "через сколько начнётся окно усреднения страйка. До этого момента ход цены " +
        "на исход НЕ влияет: он сдвигает страйк и итог на одну и ту же величину, " +
        "поэтому исход — монетка, а переоценка стакана — не по делу",
    };
  }
  const unknown = Math.ceil(p.strike_unknown_ms / 1000);
  if (unknown > 0) {
    return {
      label: "страйк неизвестен",
      seconds: unknown,
      title:
        "сколько секунд окна страйка ещё не наступило. Каждая из них сдвигает " +
        "страйк на свою долю, а итог — целиком: именно здесь стакан следующего окна " +
        "переоценивается по делу",
    };
  }
  return {
    label: "страйк определён",
    seconds: null,
    title:
      "все секунды окна страйка прошли: число ниже совпадёт с записанным страйком, " +
      "как только источник его опубликует на открытии окна",
  };
}

/** Запас над прогнозом страйка стрелкой и величиной: «▲ 4.31 $», «= 0.00 $». */
export function preopenCushion(p: WindowPreopen | undefined): string {
  if (!p) return "—";
  return `${cushionArrow(p.cushion)} ${cushionAmount(p.cushion)}`;
}

/** Тот же запас по достроенному ряду. Пусто, когда совпал до цента. */
export function preopenAheadCushion(p: WindowPreopen | undefined): string {
  const a = p?.ahead;
  if (!a || sameCents(a.cushion, p?.cushion)) return "";
  return `${cushionArrow(a.cushion)} ${cushionAmount(a.cushion)}`;
}

/** Подсказка к запасу предстарта. */
export function preopenCushionTitle(p: WindowPreopen | undefined): string {
  const base =
    "итог окна против ПРОГНОЗА страйка, если цена замрёт. До начала окна страйка " +
    "запас ровно ноль по построению: ход цены сдвигает страйк и итог одинаково";
  const fwd = preopenAheadCushion(p);
  return fwd === "" ? base : `${base}. По достроенному ряду: ${fwd}`;
}

/**
 * Оговорка к вероятности предстарта.
 *
 * Строже, чем у живого окна, и не для порядка: закон хвоста (pricing.FlipProb)
 * подобран на снимках ЖИВОГО окна, а на предстарте не сверялся ни разу. Число
 * посчитано честно, но за его калибровку никто не поручился.
 */
export const PREOPEN_PROB_NOTE =
  "вероятность ПЕРЕВОРОТА ведущей стороны до фиксации страйка — не вероятность Up. " +
  "Разброс учитывает, что страйк ещё случаен, но закон хвоста подобран на ЖИВОМ " +
  "окне и на предстарте не сверялся. Решение по ней не принимается. Второе число — " +
  "по ряду, достроенному спотом биржи";

/** Вероятность переворота на предстарте: «38 %». Пусто — σ не оценена. */
export function preopenProbLabel(p: WindowPreopen | undefined): string {
  return percentOf(p?.prob_flip);
}

/** Та же вероятность по достроенному ряду. Пусто, когда совпала до процента. */
export function preopenAheadProbLabel(p: WindowPreopen | undefined): string {
  const shown = percentOf(p?.ahead?.prob_flip);
  return shown === "" || shown === preopenProbLabel(p) ? "" : shown;
}

/** Итог окна, если цена замрёт, и сторона, в чью пользу он сейчас. */
export function preopenProjected(p: WindowPreopen | undefined): string | undefined {
  return p?.projected || undefined;
}

export function preopenAheadProjected(p: WindowPreopen | undefined): string | undefined {
  const a = p?.ahead;
  if (!a?.projected || sameCents(a.projected, p?.projected)) return undefined;
  return a.projected;
}

/** Ведущая сторона по знаку запаса. Пусто при нулевом — стороны ещё нет. */
export function preopenSide(p: WindowPreopen | undefined): string {
  const c = parse(p?.cushion);
  if (c === null || c === 0) return "";
  return c > 0 ? "Up" : "Down";
}

/** Потеря секунд окна страйка словами. Пусто — терять нечего. */
export function preopenLostNote(p: WindowPreopen | undefined): string {
  const lost = p?.strike_lost ?? 0;
  return lost > 0 ? `в окне страйка потеряно секунд: ${lost}` : "";
}

function sameCents(a: string | undefined, b: string | undefined): boolean {
  const x = parse(a);
  const y = parse(b);
  if (x === null || y === null) return true;
  return (x / SCALE).toFixed(2) === (y / SCALE).toFixed(2);
}
